/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Universal Multi-Gateway Checkout Schemas & DTOs (Task 6.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

export const UniversalPaymentGatewaySchema = z.enum([
  'stripe',
  'paypal',
  'paddle',
  'bank_transfer',
]);

export type UniversalPaymentGateway = z.infer<typeof UniversalPaymentGatewaySchema>;

/**
 * Request schema for creating a multi-gateway checkout order
 */
export const UniversalCheckoutRequestSchema = z
  .object({
    planId: z.string().min(1, 'Plan or item ID is required').openapi({
      description: 'Subscription Plan ID or Token Pack ID',
      example: 'pro-creator',
    }),
    gateway: UniversalPaymentGatewaySchema.openapi({
      description: 'Selected payment gateway method',
      example: 'stripe',
    }),
    billingInterval: z
      .enum(['monthly', 'yearly'])
      .optional()
      .default('monthly')
      .openapi({ description: 'Billing frequency', example: 'monthly' }),
    topupCredits: z
      .number()
      .positive()
      .optional()
      .openapi({ description: 'Credits amount if purchasing a token top-up pack', example: 55000 }),
    amount: z
      .number()
      .positive()
      .optional()
      .openapi({ description: 'Expected charge amount in USD', example: 39 }),
    currency: z
      .string()
      .optional()
      .default('USD')
      .openapi({ description: 'Currency code', example: 'USD' }),
    successUrl: z
      .string()
      .optional()
      .openapi({ description: 'Return URL upon payment success' }),
    cancelUrl: z
      .string()
      .optional()
      .openapi({ description: 'Return URL upon payment cancellation' }),
  })
  .openapi('UniversalCheckoutRequest');

export type UniversalCheckoutRequest = z.infer<typeof UniversalCheckoutRequestSchema>;

/**
 * Bank transfer metadata returned when selecting VietQR/wire
 */
export const BankTransferDetailsSchema = z
  .object({
    bankName: z.string().openapi({ example: 'Vietcombank' }),
    accountNumber: z.string().openapi({ example: '1029384756' }),
    accountHolder: z.string().openapi({ example: 'FOUR ELEMENTS CORP' }),
    transferCode: z.string().openapi({ example: 'SPH_ORD9021' }),
    qrUrl: z.string().openapi({ example: 'https://img.vietqr.io/image/970436-1029384756-compact2.png' }),
    amountVnd: z.number().openapi({ example: 975000 }),
    instructions: z.string().openapi({ example: 'Please include transferCode in wire description.' }),
  })
  .openapi('BankTransferDetails');

export type BankTransferDetails = z.infer<typeof BankTransferDetailsSchema>;

/**
 * Unified checkout response returned to client
 */
export const UniversalCheckoutResponseSchema = z
  .object({
    orderId: z.string().openapi({ description: 'Created Order ID in D1 database', example: 'ord_9021a8b7' }),
    gateway: UniversalPaymentGatewaySchema.openapi({ description: 'Payment gateway handling transaction' }),
    amount: z.number().openapi({ description: 'Order total amount in USD', example: 39 }),
    currency: z.string().openapi({ description: 'Order currency', example: 'USD' }),
    status: z.enum(['pending', 'paid']).openapi({ description: 'Initial order status' }),
    checkoutUrl: z.string().nullable().optional().openapi({ description: 'Hosted checkout URL for Stripe, PayPal, or Paddle redirect' }),
    paddleTransactionId: z.string().nullable().optional().openapi({ description: 'Paddle transaction token for Paddle.js overlay checkout' }),
    bankDetails: BankTransferDetailsSchema.optional().openapi({ description: 'Bank transfer / VietQR info if bank wire chosen' }),
  })
  .openapi('UniversalCheckoutResponse');

export type UniversalCheckoutResponse = z.infer<typeof UniversalCheckoutResponseSchema>;

